import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { useMutation } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { useBlockScene, useResultsInvalidation } from '@editorHooks'
import { useModelInvalidation } from '@structuralPlanningHooks'
import { updateWall as updateWallRequest } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import { pointsMatch, proposalSourceGuid } from './utils'

/**
 * "Annehmen" action of the automatic wall-connect tool: persists the connection proposal by
 * updating the affected walls one by one via `updateWall`. The backend only accepts moving one
 * wall end per request (moving both is interpreted as a whole-wall translation and rejected when
 * the offsets differ), so each changed end is sent as its own request and unchanged ends — or
 * whole walls already matching their proposal — cause no request at all. The model is refetched
 * once at the end (also after a mid-sequence failure, so already-applied walls stay consistent
 * with the server).
 */
const useApplyWallConnectionProposals = (): {
  applyProposals: (proposals: ShapeObject[]) => void
  isApplying: boolean
} => {
  const { projectId }: { projectId?: string } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const invalidateModel = useModelInvalidation()
  const invalidateResults = useResultsInvalidation()

  const { mutate: applyProposals, isLoading: isApplying } = useMutation(
    async (proposals: ShapeObject[]) => {
      const walls = useModelStore.getState().model.walls
      let updatedCount = 0

      for (const proposal of proposals) {
        const wall = walls.find(current => current.guid === proposalSourceGuid(proposal.guid))
        if (!wall) continue

        const [currentStart, currentEnd] = wall.shape.points
        const [proposalStart, proposalEnd] = proposal.shape.points
        const startChanged = !pointsMatch(currentStart, proposalStart)
        const endChanged = !pointsMatch(currentEnd, proposalEnd)
        if (!startChanged && !endChanged) continue

        const placement = wall.placement || 'External'

        // One request per changed end; a wall adjusted at both ends is updated in two steps.
        let latest: ShapeObject = wall
        if (startChanged) {
          const newPosition: NewWallPosition = {
            new_start: { x: proposalStart.x, y: proposalStart.y },
            new_end: { x: currentEnd.x, y: currentEnd.y },
            placement,
          }
          latest = await updateWallRequest.request(projectId as string, wall.guid, newPosition)
        }
        if (endChanged) {
          // Source the unchanged start from the server response of the first step so the request
          // is guaranteed to move only the end.
          const [latestStart] = latest.shape.points
          const newPosition: NewWallPosition = {
            new_start: { x: latestStart.x, y: latestStart.y },
            new_end: { x: proposalEnd.x, y: proposalEnd.y },
            placement,
          }
          await updateWallRequest.request(projectId as string, wall.guid, newPosition)
        }
        updatedCount += 1
      }

      return updatedCount
    },
    {
      mutationKey: ['applyWallConnectionProposals', projectId],
      meta: {
        title: 'Wandverbindungen übernehmen',
        description: 'Übernimmt den automatischen Verbindungsvorschlag für die Wände.',
      },
      onSuccess: async (updatedCount: number) => {
        await invalidateModel(projectId as string)
        invalidateResults(projectId as string)
        enqueueSnackbar(
          updatedCount === 0
            ? 'Keine Änderungen notwendig — alle Wände entsprechen bereits dem Vorschlag'
            : `${updatedCount} ${updatedCount === 1 ? 'Wand wurde' : 'Wände wurden'} angepasst`,
          { variant: 'success' },
        )
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Übernehmen der Wandverbindungen'), {
          variant: 'error',
        })
        // Walls updated before the failure are already persisted — refetch so the model and
        // results reflect the server state.
        invalidateModel(projectId as string)
        invalidateResults(projectId as string)
      },
    },
  )

  // Block the scene while the walls are being updated so no other interaction races the sequence.
  useBlockScene(isApplying)

  return { applyProposals, isApplying }
}

export default useApplyWallConnectionProposals
